import urls from "global/constants/UrlConstants";
import { getCallbackKeyFromUrl, isTruthy } from "helpers/methods";
import { CustomField, RequestFile, SignRequest } from "models/interfaces";
import {
  captureApiToPostHog,
  getEventNameForEndpoint,
} from "utils/posthogApiCapture";
import {
  fetchDocument,
  getAuthFileCallParams,
  getCallParams,
  getFileCallParams,
  getNoAuthCallParams,
  getNoAuthFileCallParams,
  getNoAuthSearchedCallParams,
  makeCall,
  makeCallFile,
} from "utils/service";
import { embedEditorRequestId, isEmbedEditorMode } from "embed/EmbedEditorMode";
import {
  askEmbedPdf,
  detectEmbedFields,
  fetchEmbedDimensions,
  fetchEmbedDocumentLimit,
  fetchEmbedDocumentSummary,
  fetchEmbedDocumentUrl,
  recordEmbedSmsConsent,
  uploadEmbedDocument,
} from "embed/embedService";
import {
  PresendReviewRequest,
  PresendReviewResponse,
} from "screens/SignRequest/components/PresendReview/PresendReviewTypes";

export const fetchCustomFields = async (): Promise<CustomField[]> => {
  const callParams = await getCallParams("GET");
  const response = await makeCall(urls.FETCH_CUSTOM_FIELDS, callParams);
  return Array.isArray(response) ? response : [];
};

export const createCustomField = async (
  customField: CustomField,
): Promise<string> => {
  const callParams = await getCallParams("POST", customField);
  const response = await makeCall(urls.ADD_CUSTOM_FIELD, callParams);
  return response?.message ?? response;
};

export const updateCustomField = async (
  id: string,
  customField: CustomField,
) => {
  const callParams = await getCallParams("POST", customField);
  return await makeCall(`${urls.UPDATE_CUSTOM_FIELD}/${id}`, callParams);
};

export const deleteCustomField = async (id: string) => {
  const callParams = await getCallParams("DELETE");
  return await makeCall(`${urls.DELETE_CUSTOM_FIELD}/${id}`, callParams);
};

export const getEnvelopeId = async () => {
  // Embedded wizard: the draft already exists (the partner created it); there is no new envelope to ask for.
  if (isEmbedEditorMode()) return { message: embedEditorRequestId() };
  try {
    const url = urls.GET_ENVELOPE_ID;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const copyFiles = async (
  sourceId: string,
  destinationId: string,
  isSourceRequest: boolean,
  isDestinationRequest: boolean,
) => {
  try {
    const url = `${urls.COPY_TEMPLATE_FILES}/from/${sourceId}/to/${destinationId}/isSourceRequest/${isSourceRequest}/isDestinationRequest/${isDestinationRequest}`;
    const callParams = await getCallParams("POST");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const uploadUnsignedPdf = async (
  formData: any,
  isrequest: boolean,
  envelopeId: string,
  signal: AbortSignal,
) => {
  // Embedded wizard: the file goes to the session's own draft, whatever envelope id the screen holds.
  if (isEmbedEditorMode()) {
    return uploadEmbedDocument(embedEditorRequestId(), formData, signal);
  }
  const url = `${urls.UPLOAD_UNSIGNED_PDF}/${isrequest}/envelopeId/${envelopeId}`;
  try {
    const callParams: any = await getFileCallParams("POST", formData);
    callParams.signal = signal;
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const detectFields = async (
  envelopeId: string,
  unsignedPdf: string,
  // A template's upload is stored in the template folder, not the request's.
  fromRequest: boolean = true,
) => {
  // Embedded wizard: the draft's own embed endpoint (X-Embed-Session).
  if (isEmbedEditorMode()) return detectEmbedFields(embedEditorRequestId(), unsignedPdf);
  const url = `${urls.DETECT_FIELDS}/envelopeId/${envelopeId}?fromRequest=${fromRequest !== false}`;
  try {
    const callParams = await getCallParams("POST", { unsignedPdf });
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Review timeout: the AI's own ceiling is 20 s and core's read timeout 30 s, so core answers first. */
const PRESEND_REVIEW_TIMEOUT_MS = 35000;

/** The AI pre-send review of the documents about to be sent. Advisory: the caller never blocks on it. */
export const reviewBeforeSend = async (
  envelopeId: string,
  payload: PresendReviewRequest,
): Promise<PresendReviewResponse> => {
  try {
    const callParams = await getCallParams("POST", payload);
    const response = await makeCall(
      `${urls.PRESEND_REVIEW}/${envelopeId}`,
      callParams,
      true,
      { timeoutMs: PRESEND_REVIEW_TIMEOUT_MS },
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getAppPlanLimitByEntity = async (entity: string) => {
  const url = `${urls.APP_PLAN_LIMIT_BY_ENTITY}/${entity}`;
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getContactexclude = async (
  searchText: string,
  excludeIds: string[],
  count: number = 10,
  page: number = 1,
) => {
  const url = `${urls.GET_CONTACTS_EXCLUDE}/${searchText}/limit/${count}/page/${page}`;
  try {
    const callParams = await getCallParams("POST", excludeIds);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const fetchDocumentDimensions = async (
  requestFile: any,
  isRequest: boolean,
  envelopeId: string,
  isPublic: boolean,
  abortSignal?: AbortSignal | undefined,
) => {
  // Embedded editor: the draft's documents are reached through the embed session,
  // scoped server-side to that one draft. Signer (isPublic) calls are unchanged.
  if (!isPublic && isEmbedEditorMode()) {
    return fetchEmbedDimensions(embedEditorRequestId(), requestFile);
  }
  const url = isPublic
    ? `${urls.UNSIGNED_DOC_DIMENSIONS_CALLBACK}/requestToken/${envelopeId}?id=${encodeURIComponent(envelopeId)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`
    : isRequest ? `${urls.UNSIGNED_DOC_DIMENSIONS}/${isRequest}/envelopeId/${envelopeId}` : `${urls.TEMPLATE_UNSIGNED_DOC_DIMENSIONS}/${isRequest}/envelopeId/${envelopeId}`;
  try {
    const callParams: any = await getCallParams("POST", requestFile);
    callParams.signal = abortSignal;
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const sendRequest = async (request: SignRequest) => {
  const url = `${urls.SEND_SIGN_REQ}/true`;
  try {
    const callParams = await getCallParams("POST", {
      ...request,
      externalSignature:
        "f597a4823b00573cf267b119b4047d8e_softsages_keystore.pem",
      signaturePassword: "SoftSages@201",
    });
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateSignRequest = async (signRequest: SignRequest) => {
  const url = `${urls.UPDATE_SIGN_REQ}`;
  try {
    const callParams = await getCallParams("POST", {
      ...signRequest,
    });
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Record the sender's confirmation that the SMS recipients agreed to be texted.
 * Required before an SMS-delivery request is sent — SMS delivery is rejected
 * (403) without a recorded consent. Idempotent; numbers may be sent formatted
 * (normalized server-side).
 */
export const recordSmsConsent = async (body: {
  consentReferenceId: string;
  phoneNumbers: string[];
  confirmed: boolean;
}) => {
  // Embedded wizard: the draft's own embed endpoint (X-Embed-Session).
  if (isEmbedEditorMode()) {
    return recordEmbedSmsConsent(embedEditorRequestId(), body);
  }
  const url = `${urls.SMS_CONSENT}`;
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const getIsAutoReminderConfig = async () => {
  try {
    const url = `${urls.GET_ACCOUNT_CONFIG}/is/auto/reminder/on`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getReminderConfig = async () => {
  try {
    const url = `${urls.GET_ACCOUNT_CONFIG}/reminders`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getExpirationDays = async () => {
  try {
    const url = `${urls.GET_ACCOUNT_CONFIG}/expirationDays`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDocumentUrl = async (
  requestFile: any,
  isRequest: boolean,
  envelopeId: string,
  isPublic: boolean,
  abortSignal?: AbortSignal | undefined,
) => {
  if (!isPublic && isEmbedEditorMode()) {
    return fetchEmbedDocumentUrl(embedEditorRequestId(), requestFile);
  }
  const url = isPublic
    ? `${urls.UNSIGEND_DOC_URL_CALLBACK}/requestToken/${envelopeId}?id=${encodeURIComponent(envelopeId)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`
    : isRequest
      ? `${urls.UNSIGEND_DOC_URL}/${isRequest}/envelopeId/${envelopeId}`
      : `${urls.TEMPLATE_UNSIGEND_DOC_URL}/${isRequest}/envelopeId/${envelopeId}`;
  try {
    const callParams: any = await (isPublic
      ? getNoAuthCallParams("POST", requestFile)
      : getCallParams("POST", requestFile));
    callParams.signal = abortSignal;
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSignRequest = async (token: any) => {
  const url = `${urls.FETCH_SIGN_REQ}/${token}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export async function fetchDimensions(
  file: any,
  token: string,
  pageNo: number,
) {
  const url = `${urls.SIGNED_DOC_DIMENSIONS}/${token}/pageNo/${pageNo}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("POST", file);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchSignedPdf(requestToken: any, file: RequestFile) {
  const url = `${urls.DOWNLOAD_SIGN_PDF}/${requestToken}/signed/true/image/false?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const response = await fetchDocument(url, "POST", true, file);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchSignImage(token: any) {
  const url = `${urls.FETCH_SIGN_IMG}/${token}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const response = await fetchDocument(url, "GET", true);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchSelfSignSignImage() {
  const url = `${urls.SELF_SIGN_FETCH_SIGN_IMG}`;
  try {
    const response = await fetchDocument(url, "GET", false);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchSelfSignInitialsImage() {
  const url = `${urls.SELF_SIGN_FETCH_INITIALS_IMG}`;
  try {
    const response = await fetchDocument(url, "GET", false);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchInitialsImage(token: any) {
  const url = `${urls.FETCH_INITIALS_IMG}/${token}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const response = await fetchDocument(url, "GET", true);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchMergedSignedPdfAsZipCallback(
  requestToken: any,
  signRequest: any,
) {
  const url = `${urls.DOWNLOAD_DOCS_AS_ZIP_CALLBACK}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("POST", signRequest);
    const response = await makeCallFile(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export const fetchMergedSignedPdfAsZip = async (file: any) => {
  const url = `${urls.DOWNLOAD_SIGN_AS_ZIP}`;
  try {
    const callParams = await getCallParams("POST", file);
    const response = await makeCallFile(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export async function fetchMergedSignedDoc(requestToken: any, file: any) {
  const url = `${urls.MERGED_SIGNED_PDF}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const response = await fetchDocument(url, "POST", file);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchRoleFromToken(requestToken: string) {
  const url = `${urls.DECRYPT_SIGN_TOKEN}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = getNoAuthCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function sendOtpRequest(requestToken: string) {
  const url = `${urls.SEND_OTP}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export type VerifyOtpResponse = {
  verified: boolean;
  code: number;
  shortMessage?: string;
  message?: string;
};

export async function verifyOtpRequest(
  requestToken: string,
  body: {
    name: string;
    email: string;
    order: number;
    id: number;
    color: string;
    requestSecurity: {
      needSecuredAccess: boolean;
      securityCode: string | null;
      needOTPValidation: boolean;
      otp: string;
    };
  },
): Promise<VerifyOtpResponse> {
  const url = `${urls.VERIFY_OTP}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response as VerifyOtpResponse;
  } catch (error: any) {
    throw error;
  }
}

export async function sendPhoneOtpRequest(requestToken: string) {
  const url = `${urls.SEND_PHONE_OTP}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function verifyPhoneOtpRequest(
  requestToken: string,
  body: {
    name: string;
    email: string;
    order: number;
    id: number;
    color: string;
    requestSecurity: {
      needSecuredAccess: boolean;
      securityCode: string | null;
      needOTPValidation: boolean;
      needPhoneValidation: boolean;
      otp: string;
    };
  },
): Promise<VerifyOtpResponse> {
  const url = `${urls.VERIFY_PHONE_OTP}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response as VerifyOtpResponse;
  } catch (error: any) {
    throw error;
  }
}

export type VerifySecurityCodeResponse = {
  verified: boolean;
  code: number;
  error?: string;
  message?: string;
};

export async function verifySecurityCodeRequest(
  requestToken: string,
  body: {
    name: string;
    email: string;
    order: number;
    id: number;
    color: string;
    requestSecurity: {
      needSecuredAccess: boolean;
      securityCode: string;
      needOTPValidation: boolean;
    };
  },
): Promise<VerifySecurityCodeResponse> {
  const url = `${urls.VERIFY_SECURITY_CODE}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("POST", body);
    const response = await makeCall(url, callParams);
    return response as VerifySecurityCodeResponse;
  } catch (error: any) {
    throw error;
  }
}

export async function approveRejectSignRequest(
  isApprove: boolean,
  requestToken: string,
) {
  const url = `${urls.APPROVE_REJECT_SIGN_REQUEST}/${
    isApprove ? "approve" : "reject"
  }/request/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getCallParams("POST", null);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function signPdf(token: any, body: any) {
  const url = `${urls.SIGN_PDF}/${token}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  const startTime = Date.now();
  try {
    const callParams = await getNoAuthCallParams("POST", body);
    const requestPayload = ((): unknown => {
      try {
        const b = callParams?.body;
        if (b == null) return undefined;
        return typeof b === "string" ? JSON.parse(b) : b;
      } catch {
        return undefined;
      }
    })();
    const response = await fetch(url, callParams);
    const responseData = response.ok
      ? await response
          .clone()
          .json()
          .catch(() => null)
      : undefined;
    captureApiToPostHog(getEventNameForEndpoint(url), {
      url,
      method: "POST",
      requestPayload,
      response: responseData,
      statusCode: response.status,
      durationMs: Date.now() - startTime,
    });
    return response;
  } catch (error: any) {
    captureApiToPostHog(getEventNameForEndpoint(url), {
      url,
      method: "POST",
      requestPayload: body,
      statusCode: undefined,
      errorMessage: error?.message ?? String(error),
      durationMs: Date.now() - startTime,
    });
    throw error;
  }
}

export async function declineRequest(
  token: string,
  reason: string,
  declineRequestMap: Record<string, { highlights: any[] }>,
) {
  const url = `${urls.DECLINE_REQUEST}/requestToken/${token}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const processedMap: Record<string, any[]> = {};

    Object.entries(declineRequestMap).forEach(([documentName, declineReq]) => {
      const highlights = declineReq.highlights || [];
      if (highlights.length === 0) {
        processedMap[documentName] = [];
        return;
      }

      const first = highlights[0] as any;
      const simplifiedHighlights =
        first && first.position
          ? highlights
              .flatMap((h: any) => {
                const position = h.position;
                if (!position) return [];

                const rectsToUse =
                  position.rects && position.rects.length > 0
                    ? position.rects
                    : position.boundingRect
                      ? [position.boundingRect]
                      : [];

                const pageNumber =
                  position.pageNumber || position.boundingRect?.pageNumber;
                if (!pageNumber) return [];

                return rectsToUse.map((rect: any) => ({
                  x1: rect.x1,
                  y1: rect.y1,
                  x2: rect.x2,
                  y2: rect.y2,
                  width: rect.width,
                  height: rect.height,
                  pageNumber: pageNumber,
                }));
              })
              .filter((h: any) => h != null)
          : highlights;

      processedMap[documentName] = simplifiedHighlights;
    });

    const requestBody = {
      reason: reason,
      highlights: processedMap, // Map<String, Highlight[]>
    };

    const callParams = getNoAuthSearchedCallParams(
      "POST",
      JSON.stringify(requestBody),
    );

    const response = await fetch(url, callParams);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function delegateRequest(
  token: string,
  email: string,
  name: string,
  message: string,
) {
  const url = `${urls.DELEGATE_REQUEST}/${token}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("POST", {
      email,
      name,
      privateMessage: isTruthy(message) ? message : "",
    });
    const response = await fetch(url, callParams);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function saveFieldData(signRequest: any, requestToken: string) {
  const url = `${urls.SAVE_FIELD_DATA}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("POST", {
      ...signRequest,
    });
    const response = await makeCall(url, callParams, true, {
      captureToPostHog: true,
    });
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchDocumentSummaryCallback(
  requestToken: string,
  requestFile: RequestFile,
) {
  const url = `${urls.FETCH_DOCUMENT_SUMMARY_CALLBACK}/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthCallParams("POST", requestFile);
    // AI extraction can take well over the 30s default — allow up to 2 minutes.
    const response = await makeCall(url, callParams, true, {
      timeoutMs: 120000,
    });
    return response;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Summary of a SIGNED document. Same body and response as {@link fetchDocumentSummary}, but the server
 * reads `signedPdf` from the file and asks the AI's post-sign endpoint.
 */
export async function fetchSignedDocumentSummary(
  envelopeId: string,
  requestFile: RequestFile,
) {
  const url = `${urls.FETCH_SIGNED_DOCUMENT_SUMMARY}/${envelopeId}`;
  try {
    const callParams = await getCallParams("POST", requestFile);
    // AI extraction can take well over the 30s default — allow up to 2 minutes.
    const response = await makeCall(url, callParams, true, {
      timeoutMs: 120000,
    });
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function fetchDocumentSummary(
  envelopeId: string,
  requestFile: RequestFile,
) {
  // Embedded wizard: no user session, so the draft's own embed endpoint (X-Embed-Session).
  if (isEmbedEditorMode()) {
    return fetchEmbedDocumentSummary(embedEditorRequestId(), requestFile);
  }
  const url = `${urls.FETCH_DOCUMENT_SUMMARY}/${envelopeId}`;
  try {
    const callParams = await getCallParams("POST", requestFile);
    // AI extraction can take well over the 30s default — allow up to 2 minutes.
    const response = await makeCall(url, callParams, true, {
      timeoutMs: 120000,
    });
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function uploadSignImage(token: any, formData: any) {
  const url = `${urls.UPLOAD_SIGN_IMG}/${token}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthFileCallParams(formData);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function uploadSelfSignSignImage(formData: any) {
  const url = `${urls.UPLOAD_SELF_SIGN_SIGN_IMG}`;
  try {
    const callParams = await getAuthFileCallParams(formData);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function uploadInitialsImage(token: any, formData: any) {
  const url = `${urls.UPLOAD_INITIALS_IMG}/${token}?id=${encodeURIComponent(token)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
  try {
    const callParams = await getNoAuthFileCallParams(formData);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function uploadSelfSignInitialsImage(formData: any) {
  const url = `${urls.UPLOAD_SELF_SIGN_INITIALS_IMG}`;
  try {
    const callParams = await getAuthFileCallParams(formData);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function saveDraftSignRequest(signRequest: any) {
  const url = `${urls.SAVE_DRAFT_SIGN_REQ}`;
  try {
    const callParams = await getCallParams("POST", {
      ...signRequest,
      externalSignature:
        "f597a4823b00573cf267b119b4047d8e_softsages_keystore.pem",
      signaturePassword: "SoftSages@201",
    });
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function sendPdfSignRequest(
  pdfSignRequest: any,
  isRequest: boolean,
) {
  const url = `${urls.SEND_SIGN_REQ}/${isRequest}`;
  try {
    const callParams = await getCallParams("POST", {
      ...pdfSignRequest,
      externalSignature:
        "f597a4823b00573cf267b119b4047d8e_softsages_keystore.pem",
      signaturePassword: "SoftSages@201",
    });
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function sendBulkSignRequest(signRequest: any) {
  const url = `${urls.BULK_SEND}`;
  try {
    const callParams = await getCallParams("POST", signRequest);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function sendSelfSignRequest(signRequest: any) {
  const url = `${urls.SEND_SELF_SIGN_REQ}`;
  try {
    const callParams = await getCallParams("POST", signRequest);
    const response = await makeCall(url, callParams, true, { timeoutMs: 60000 });
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function prepareSelfSignRequest(signRequest: any) {
  const url = `${urls.PREPARE_SELF_SIGN_REQ}`;
  try {
    const callParams = await getCallParams("POST", signRequest);
    const response = await makeCall(url, callParams, true, { timeoutMs: 60000 });
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function uploadSelfSignAttachment(
  requestId: string,
  fieldName: string,
  file: File,
) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("fieldName", fieldName);
  const url = `${urls.UPLOAD_SELF_SIGN_ATTACHMENT}/${encodeURIComponent(requestId)}`;
  try {
    const callParams = await getFileCallParams("POST", formData);
    return await makeCall(url, callParams);
  } catch (error: any) {
    throw error;
  }
}

export async function completeSelfSignRequest(
  requestToken: string,
  pdfFiles: any[],
) {
  const url = `${urls.COMPLETE_SELF_SIGN_REQ}/${encodeURIComponent(requestToken)}`;
  try {
    const callParams = await getCallParams("POST", pdfFiles);
    const response = await makeCall(url, callParams, true, { timeoutMs: 60000 });
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function saveTemplate(signRequest: any, isRequest: boolean) {
  const url = `${urls.SAVE_TEMPLATE}/${isRequest}`;
  try {
    const callParams = await getCallParams("POST", signRequest);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function deleteDraft(id: string) {
  const url = `${urls.DELETE_DRAFT_SIGN_REQ}/${id}`;
  try {
    const callParams = await getCallParams("DELETE");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
}

export async function getUploadDocumentLimit() {
  if (isEmbedEditorMode()) return fetchEmbedDocumentLimit(embedEditorRequestId());
  try {
    const url = `${urls.UPLOAD_DOCUMENTS_LIMIT}`;
    const callParams = await getCallParams("GET");
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function askPdf(payload: any) {
  // Embedded wizard: the draft's own embed endpoint (X-Embed-Session) instead of the user session.
  if (isEmbedEditorMode()) {
    return askEmbedPdf(embedEditorRequestId(), payload);
  }
  try {
    const url = `${urls.ASK_PDF}`;
    const callParams = await getCallParams("POST", payload);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
}

/** Chat against the SIGNED copies. Same payload and response shape as {@link askPdf}. */
export async function askSignedPdf(payload: any) {
  try {
    const url = `${urls.ASK_SIGNED_PDF}`;
    const callParams = await getCallParams("POST", payload);
    // The AI can take a while on a long contract — same allowance as the summary call.
    const response = await makeCall(url, callParams, true, {
      timeoutMs: 120000,
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function askPdfCallback(payload: any, requestToken: string) {
  try {
    const url = `${urls.ASK_PDF_CALLBACK}/requestToken/${requestToken}?id=${encodeURIComponent(requestToken)}&key=${encodeURIComponent(getCallbackKeyFromUrl())}`;
    // Public/guest signer context has no access token, so the authenticated
    // getCallParams would drop the headers entirely and the browser would send
    // the body as text/plain → 415. Use the no-auth params (application/json),
    // matching every other public *Callback endpoint in this service.
    const callParams = getNoAuthCallParams("POST", payload);
    const response = await makeCall(url, callParams);
    return response;
  } catch (error) {
    throw error;
  }
}
